import { TrackerChangeEntry, TrackerData, TrackerDelta } from "./types";

export const serialToAddress = (serial: string): string | null => {
  if(serial.match(/^\d{11}$/)) {
    const fa = Number(serial.substr(0, 5));
    const sn = Number(serial.substr(5));

    const raw = fa + (sn << 18);
    const hex = raw.toString(16).padStart(8, '0');

    const swapped = hex.match(/.{2}/g)!.reverse().join('');

    return swapped;
  } else if(serial.match(/^\d{10}$/)) {
    const raw = Number(serial);
    const hex = raw.toString(16).padStart(8, '0');

    const swapped = hex.match(/.{2}/g)!.reverse().join('');

    return swapped;
  } else {
    return null;
  }
};

export const trackerArrayToMap = (trackers: TrackerData[]): Map<string,TrackerData> => {
  return new Map(trackers.map((tracker): [string, TrackerData] => [tracker.id, tracker]));
}

export const calcTrackerChanges = (trackers: TrackerData[], deltas: Map<string,TrackerDelta>, merge=true): TrackerChangeEntry[] => {
  const trackerMap = trackerArrayToMap(trackers);

  const unmappedTrackers = new Set<string>(trackerMap.keys());

  const changes = Array<TrackerChangeEntry>();

  for(const [id, delta] of deltas.entries()) {
    const tracker = trackerMap.get(id);

    if(tracker == null) {
      if(!merge) {
        // tracker not found, create it
        changes.push({ id, delta });
      }

      continue;
    }

    // update tracker
    changes.push({ id, tracker, delta });

    // mark tracker as mapped
    unmappedTrackers.delete(id);
  }

  if(!merge) {
    for(const id of unmappedTrackers) {
      // remove unmapped trackers
      const tracker = trackerMap.get(id)!;
      changes.push({ id, tracker });
    }
  }

  return changes;
}

export const applyTrackerChanges = (trackers: TrackerData[], changes: TrackerChangeEntry[]): TrackerData[] => {
  if(changes.length === 0) {
    return trackers;
  }

  const trackerMap = trackerArrayToMap(trackers);

  for(const { id, tracker, delta } of changes) {
    if(delta == null) {
      trackerMap.delete(id);
      continue;
    }

    const newTracker = tracker != null ?  { ...tracker } : { id };

    for(const [key, value] of delta.entries()) {
      if(value != null) {
        newTracker[key] = value;
      } else {
        delete newTracker[key];
      }
    }

    trackerMap.set(id, newTracker);
  }

  return Array.from(trackerMap.values());
};

export type getBoxMappingFun = (ids: string[]) => Promise<Map<string,string>>;

export const enrichTrackerAddresses = async (changes: TrackerChangeEntry[], getBoxMapping: getBoxMappingFun): Promise<TrackerChangeEntry[]> => {
  const lookupBoxes = new Set<string>();
  const handledTrackers = new Set<string>();

  for(const { id, tracker, delta } of changes) {
    if(delta == null) {
      // this is a delete, nothing to do here
      continue;
    }

    if(!delta.has('controller')) {
      // delta has no box serial to look up
      continue;
    }

    if(delta.has('address')) {
      // delta has address already set
      continue;
    }

    const deltaController = delta.get('controller')

    if(tracker != null && deltaController === tracker.controller && tracker.address != null) {
      // tracker already has that controller and an address for it
      continue;
    }

    handledTrackers.add(id);
    lookupBoxes.add(String(deltaController));
  }

  if(lookupBoxes.size === 0) {
    return changes;
  }

  const serials = await getBoxMapping(Array.from(lookupBoxes.keys()));

  return changes.map((change): TrackerChangeEntry => {
    const { id, delta } = change;

    if(delta == null) {
      return change;
    }

    const controller = String(delta.get('controller'));

    if(!handledTrackers.has(id)) {
      return change;
    }

    const circuit = serials.get(controller);

    const newDelta = new Map(delta);
    newDelta.set('address', circuit != null ? serialToAddress(circuit) : null);

    return {
      ...change,
      delta: newDelta,
    }
  });
};

export const trackersToDeltas = (trackers: TrackerData[]): Map<string,TrackerDelta> => {
  const entries = trackers.map((tracker): [string, TrackerDelta] => {
    const { id, ...other } = tracker;

    return [id, new Map(Object.entries(other))];
  });

  return new Map(entries)
}
